import { Grant } from '../types';
import { API_CONFIG } from './apiConfig';

const FALLBACK_GRANTS: Grant[] = [
  {
    id: 'sbir-2024-01',
    title: 'Small Business Innovation Research Program',
    description: 'Research and development funding for small businesses in technology and innovation.',
    amount: 150000,
    deadline: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString(),
    matchScore: 0,
    requiredMaterials: [
      'Research Proposal',
      'Budget Documentation',
      'Business Registration',
      'Team Qualifications'
    ],
    applicationUrl: 'https://www.sbir.gov/apply',
    provider: 'Small Business Administration',
    category: 'technology',
    eligibility: 'Small businesses in technology and innovation sectors, including minority-owned, women-owned, and veteran-owned businesses',
    fundingType: 'federal',
    postDate: new Date().toISOString(),
    source: 'SBIR'
  },
  {
    id: 'eda-2024-01',
    title: 'Economic Development Administration Grant',
    description: 'Funding for economic development projects that support regional economic growth.',
    amount: 500000,
    deadline: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    matchScore: 0,
    requiredMaterials: [
      'Project Proposal',
      'Economic Impact Analysis',
      'Budget Documentation',
      'Community Support Letters'
    ],
    applicationUrl: 'https://www.eda.gov/funding-opportunities',
    provider: 'Economic Development Administration',
    category: 'economic development',
    eligibility: 'State and local governments, nonprofits, indigenous tribes, minority-owned businesses',
    fundingType: 'federal',
    postDate: new Date().toISOString(),
    source: 'EDA'
  },
  {
    id: 'mbda-2024-01',
    title: 'Minority Business Development Grant',
    description: 'Support for minority-owned businesses to expand operations and create jobs.',
    amount: 300000,
    deadline: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
    matchScore: 0,
    requiredMaterials: [
      'Business Plan',
      'Financial Statements',
      'Minority Ownership Documentation',
      'Job Creation Projections'
    ],
    applicationUrl: 'https://www.mbda.gov/grants',
    provider: 'Minority Business Development Agency',
    category: 'business development',
    eligibility: 'Minority-owned businesses, including BIPOC, women-owned, and disadvantaged businesses',
    fundingType: 'federal',
    postDate: new Date().toISOString(),
    source: 'MBDA'
  }
];

export async function fetchFederalGrants(filters: {
  state?: string;
  category?: string;
  demographics?: string[];
}): Promise<Grant[]> {
  try {
    // Attempt to fetch from APIs
    const [grantsGovResponse, samGovResponse] = await Promise.all([
      fetch(`${API_CONFIG.GRANTS_GOV.BASE_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_CONFIG.GRANTS_GOV.API_KEY}`
        },
        body: JSON.stringify({
          keyword: filters.category || '',
          state: filters.state || '',
          limit: 50
        })
      }).then(res => res.json()).catch(() => ({ opportunities: [] })),
      
      fetch(`${API_CONFIG.SAM_GOV.BASE_URL}`, {
        headers: {
          'api_key': API_CONFIG.SAM_GOV.API_KEY
        }
      }).then(res => res.json()).catch(() => ({ opportunities: [] }))
    ]);

    const grants = [];

    // Add API results if available
    if (grantsGovResponse.opportunities?.length) {
      grants.push(...grantsGovResponse.opportunities.map((opp: any) => ({
        id: `grants-gov-${opp.id}`,
        title: opp.title,
        description: opp.description,
        amount: opp.award_ceiling || 0,
        deadline: opp.close_date,
        matchScore: 0,
        requiredMaterials: opp.required_documents || [],
        applicationUrl: opp.application_url,
        provider: opp.agency,
        category: opp.category,
        eligibility: opp.eligibility,
        fundingType: 'federal',
        postDate: opp.post_date,
        source: 'grants.gov'
      })));
    }

    if (samGovResponse.opportunities?.length) {
      grants.push(...samGovResponse.opportunities.map((opp: any) => ({
        id: `sam-gov-${opp.id}`,
        title: opp.title,
        description: opp.description,
        amount: opp.award_amount || 0,
        deadline: opp.response_deadline,
        matchScore: 0,
        requiredMaterials: opp.required_documents || [],
        applicationUrl: opp.listing_url,
        provider: opp.department_name,
        category: opp.classification,
        eligibility: opp.eligibility_requirements,
        fundingType: 'federal',
        postDate: opp.posted_date,
        source: 'sam.gov'
      })));
    }

    // If no API results, use fallback data
    if (grants.length === 0) {
      return FALLBACK_GRANTS.filter(grant => {
        // Filter based on demographics if provided
        if (filters.demographics?.length) {
          return filters.demographics.some(demo => 
            grant.eligibility.toLowerCase().includes(demo.toLowerCase())
          );
        }
        return true;
      });
    }

    return grants;
  } catch (error) {
    console.error('Error fetching federal grants:', error);
    return FALLBACK_GRANTS;
  }
}